import React from "react";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Leaf,
  RefreshCw,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { companyInfo, divisionPages, servicePages } from "@/mock";

const iconByKey = {
  zap: Zap,
  refresh: RefreshCw,
  leaf: Leaf,
  building: Building2,
};

function Division({ slug }) {
  const page = divisionPages[slug];

  if (!page) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagina non trovata</h1>
          <Link to="/">
            <Button className="bg-orange-600 hover:bg-orange-700 text-white">Torna alla Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50 transition-all duration-300">
        <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_renewable-energy-hub-19/artifacts/wxu8fra2_logo-MTSenergia-arancio-1.png.webp"
              alt="MTS Energia Logo"
              className="h-14 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/#about" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Chi Siamo
            </Link>
            <Link to="/#divisions" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Servizi
            </Link>
            <Link to="/#projects" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Portfolio
            </Link>
            <Link to="/codice-etico" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Codice Etico
            </Link>
            <Link to="/#contact" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Contatti
            </Link>
            <Link to="/#contact">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white px-6 transition-all duration-300 hover:scale-105">
                Richiedi Preventivo
              </Button>
            </Link>
          </div>
        </nav>
      </header>

      <section className="relative pt-32 pb-16 overflow-hidden">
        {page.heroImage ? (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${page.heroImage})` }}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/40 to-orange-900/20" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <Badge className="mb-4 bg-white/15 text-white border border-white/25 px-4 py-2">
              {page.badge}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight drop-shadow-lg">
              {page.title}
            </h1>
            {page.subtitle ? (
              <p className="text-lg md:text-2xl text-white/85 mt-4 max-w-3xl">{page.subtitle}</p>
            ) : null}
          </div>
        </div>
      </section>

      {page.cards?.length ? (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {page.cards.map((card) => {
                const Icon = card.iconKey ? iconByKey[card.iconKey] : null;
                const hasServicePage = Boolean(servicePages?.[slug]?.[card.id]);
                const servicePath = hasServicePage ? `/${slug}/${card.id}` : null;
                const scrollToSection = () => {
                  document.getElementById(card.anchorId)?.scrollIntoView({ behavior: "smooth", block: "start" });
                };
                return (
                  servicePath ? (
                    <Link key={card.id} to={servicePath} className="block">
                      <Card className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer h-full">
                        <div className="h-40 relative">
                          <div
                            className="absolute inset-0 bg-cover bg-center"
                            style={{ backgroundImage: `url(${card.image})` }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                          {Icon ? (
                            <div className="absolute top-4 left-4 w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                          ) : null}
                        </div>
                        <CardHeader>
                          <CardTitle className="text-base">{card.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-gray-600 leading-relaxed">{card.description}</p>
                        </CardContent>
                      </Card>
                    </Link>
                  ) : (
                    <Card
                      key={card.id}
                      className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer h-full"
                      role="button"
                      tabIndex={0}
                      onClick={scrollToSection}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") scrollToSection();
                      }}
                    >
                      <div className="h-40 relative">
                        <div
                          className="absolute inset-0 bg-cover bg-center"
                          style={{ backgroundImage: `url(${card.image})` }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                        {Icon ? (
                          <div className="absolute top-4 left-4 w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center">
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                        ) : null}
                      </div>
                      <CardHeader>
                        <CardTitle className="text-base">{card.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 leading-relaxed">{card.description}</p>
                      </CardContent>
                    </Card>
                  )
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-10">
            {page.sections.map((section) => (
              <div key={section.id} id={section.anchorId} className="bg-white rounded-3xl border border-gray-100 shadow-lg p-8 md:p-10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{section.title}</h2>
                    {section.lead ? <p className="text-gray-600 mt-2">{section.lead}</p> : null}
                  </div>
                </div>

                {section.paragraphs?.length ? (
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    {section.paragraphs.map((p, idx) => (
                      <p key={idx}>{p}</p>
                    ))}
                  </div>
                ) : null}

                {section.bullets?.length ? (
                  <ul className="mt-6 space-y-3">
                    {section.bullets.map((b, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-gray-700">
                        <span className="w-2 h-2 rounded-full bg-orange-500 mt-2" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <Link to="/" className="inline-flex items-center space-x-3 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_renewable-energy-hub-19/artifacts/wxu8fra2_logo-MTSenergia-arancio-1.png.webp"
                  alt="MTS Energia Logo"
                  className="h-12 w-auto"
                />
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed">
                Professionisti delle rinnovabili dal {companyInfo.foundingYear}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Divisioni</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>
                  <Link to="/divisione-energia" className="hover:text-orange-400 transition-colors">
                    Divisione Energia
                  </Link>
                </li>
                <li>
                  <Link to="/consulting" className="hover:text-orange-400 transition-colors">
                    Consulting
                  </Link>
                </li>
                <li>
                  <Link to="/divisione-manutenzione" className="hover:text-orange-400 transition-colors">
                    Divisione Manutenzione
                  </Link>
                </li>
                <li>
                  <Link to="/revamping" className="hover:text-orange-400 transition-colors">
                    Revamping
                  </Link>
                </li>
                <li>
                  <Link to="/costruzioni" className="hover:text-orange-400 transition-colors">
                    Costruzioni
                  </Link>
                </li>
                <li>
                  <Link to="/risparmio-energetico" className="hover:text-orange-400 transition-colors">
                    Risparmio Energetico
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Azienda</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>
                  <Link to="/#about" className="hover:text-orange-400 transition-colors">
                    Chi Siamo
                  </Link>
                </li>
                <li>
                  <Link to="/#projects" className="hover:text-orange-400 transition-colors">
                    Portfolio
                  </Link>
                </li>
                <li>
                  <Link to="/#contact" className="hover:text-orange-400 transition-colors">
                    Contatti
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Contatti</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>{companyInfo.address}</li>
                <li>{companyInfo.phone}</li>
                <li>{companyInfo.email}</li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Division;
